import { Children, cloneElement, isValidElement, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, Circle } from "lucide-react";

import { ApiError, api } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { Button, Card, PageHeader, Skeleton, cx } from "../components/ui";

/**
 * The guided first-run setup, in the shape of an install wizard.
 *
 * Every step reuses an existing screen's API and saves as it goes, so progress
 * is the real data: closing the browser and coming back re-opens at the first
 * step whose records do not yet exist. "Business details" is the only required
 * step; the rest can be postponed and revisited from the My Desk checklist.
 * Nothing here is plan-gated.
 */

type StepId = "business" | "organisation" | "team" | "shifts" | "leave" | "review";

const STEP_ORDER: StepId[] = ["business", "organisation", "team", "shifts", "leave", "review"];

const STEP_META: Record<StepId, { title: string; body: string; optional: boolean }> = {
  business: {
    title: "Business details",
    body: "Your legal name, contact and tax details. These print on your invoices.",
    optional: false,
  },
  organisation: {
    title: "Organisation",
    body: "Departments group your team; a work location is where attendance is recorded.",
    optional: true,
  },
  team: {
    title: "Your team",
    body: "Add the people who work with you. Your owner record already exists.",
    optional: true,
  },
  shifts: {
    title: "Shifts",
    body: "A starter shift is enough to roster the team; you can add more later.",
    optional: true,
  },
  leave: {
    title: "Leave",
    body: "Your account starts with casual, sick, privilege and unpaid leave types.",
    optional: true,
  },
  review: {
    title: "Ready",
    body: "Review what is set up. Optional steps can stay unfinished.",
    optional: false,
  },
};

const inputClass =
  "w-full rounded-md border-0 px-3 py-2 text-sm ring-1 ring-inset ring-slate-300 focus:ring-2 focus:ring-inset focus:ring-slate-900";
const invalidInputClass = "ring-rose-400 focus:ring-rose-400";

const BUSINESS_DETAILS_FIELDS = [
  "legal_name",
  "phone",
  "website",
  "billing_email",
  "tax_identifier",
  "address_line1",
  "city",
  "state",
  "postal_code",
  "country_code",
] as const;

export type BusinessDetailsField = (typeof BUSINESS_DETAILS_FIELDS)[number];
export type BusinessDetailsValues = Record<BusinessDetailsField, string>;
export type BusinessDetailsErrors = Partial<Record<BusinessDetailsField, string[]>>;

export type BusinessDetailsSaveResult =
  | { ok: true }
  | { ok: false; fieldErrors: BusinessDetailsErrors; message: string | null };

export function businessDetailsValues(source: Record<string, unknown>): BusinessDetailsValues {
  const value = (key: BusinessDetailsField) => String(source[key] ?? "").trim();
  return {
    legal_name: value("legal_name"),
    phone: value("phone"),
    website: value("website"),
    billing_email: value("billing_email"),
    tax_identifier: value("tax_identifier"),
    address_line1: value("address_line1"),
    city: value("city"),
    state: value("state"),
    postal_code: value("postal_code"),
    country_code: value("country_code").toUpperCase(),
  };
}

export function normalizeWebsite(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return "";
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

function isHttpUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return (url.protocol === "http:" || url.protocol === "https:") && url.hostname !== "";
  } catch {
    return false;
  }
}

export function businessDetailsPayload(values: BusinessDetailsValues) {
  return {
    legal_name: values.legal_name.trim(),
    phone: values.phone.trim(),
    website: normalizeWebsite(values.website),
    billing_email: values.billing_email.trim(),
    tax_identifier: values.tax_identifier.trim(),
    address_line1: values.address_line1.trim(),
    city: values.city.trim(),
    state: values.state.trim(),
    postal_code: values.postal_code.trim(),
    country_code: values.country_code.trim().toUpperCase(),
  };
}

export function validateBusinessDetails(
  values: BusinessDetailsValues,
): BusinessDetailsErrors {
  const errors: BusinessDetailsErrors = {};
  const add = (field: BusinessDetailsField, message: string) => {
    errors[field] = [...(errors[field] ?? []), message];
  };

  if (!values.legal_name.trim()) {
    add("legal_name", "Enter your business's legal name.");
  } else if (values.legal_name.trim().length > 200) {
    add("legal_name", "Keep the legal name under 200 characters.");
  }

  if (values.phone.trim().length > 24) {
    add("phone", "Keep the phone number under 24 characters.");
  }

  const website = normalizeWebsite(values.website);
  if (website && !isHttpUrl(website)) {
    add("website", "Enter a valid website, e.g. https://example.com.");
  }

  const email = values.billing_email.trim();
  if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    add("billing_email", "Enter a valid billing email address.");
  }

  if (values.tax_identifier.trim().length > 32) {
    add("tax_identifier", "Keep the tax identifier under 32 characters.");
  }

  if (values.address_line1.trim().length > 160) {
    add("address_line1", "Keep the address under 160 characters.");
  }
  if (values.city.trim().length > 96) {
    add("city", "Keep the city under 96 characters.");
  }
  if (values.state.trim().length > 96) {
    add("state", "Keep the state under 96 characters.");
  }
  if (values.postal_code.trim().length > 20) {
    add("postal_code", "Keep the postal code under 20 characters.");
  }

  const country = values.country_code.trim().toUpperCase();
  if (country && !/^[A-Z]{2}$/.test(country)) {
    add("country_code", "Use a two-letter country code, e.g. IN.");
  }

  return errors;
}

export function businessDetailsFieldErrors(error: unknown): BusinessDetailsErrors {
  if (!(error instanceof ApiError)) return {};
  const errors: BusinessDetailsErrors = {};
  for (const field of BUSINESS_DETAILS_FIELDS) {
    const messages = error.fields[field];
    if (messages?.length) errors[field] = messages;
  }
  return errors;
}

function businessDetailsSubmitError(error: unknown): string | null {
  if (!(error instanceof ApiError)) {
    return error instanceof Error ? error.message : "Could not save business details.";
  }

  if (Object.keys(businessDetailsFieldErrors(error)).length > 0) return null;

  const formMessages = [
    ...(error.fields.non_field_errors ?? []),
    ...(error.fields.detail ?? []),
  ];
  if (formMessages.length > 0) return formMessages.join(" ");

  const unhandled = Object.entries(error.fields)
    .filter(
      ([field]) =>
        field !== "non_field_errors" &&
        field !== "detail" &&
        !(BUSINESS_DETAILS_FIELDS as readonly string[]).includes(field),
    )
    .flatMap(([, messages]) => messages);
  if (unhandled.length > 0) return unhandled.join(" ");

  return error.message;
}

export async function submitBusinessDetails(
  values: BusinessDetailsValues,
  save: (body: ReturnType<typeof businessDetailsPayload>) => Promise<unknown>,
): Promise<BusinessDetailsSaveResult> {
  const fieldErrors = validateBusinessDetails(values);
  if (Object.keys(fieldErrors).length > 0) {
    return { ok: false, fieldErrors, message: null };
  }

  try {
    await save(businessDetailsPayload(values));
    return { ok: true };
  } catch (error) {
    return {
      ok: false,
      fieldErrors: businessDetailsFieldErrors(error),
      message: businessDetailsSubmitError(error),
    };
  }
}

export function SetupWizardPage() {
  const { user } = useAuth();
  const accountId = user?.account?.id;
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const account = useQuery({
    queryKey: ["account", accountId],
    queryFn: ({ signal }) => api.account(accountId!, signal),
    enabled: Boolean(accountId),
  });
  const employees = useQuery({
    queryKey: ["setup-wizard-employees"],
    queryFn: ({ signal }) => api.employees(signal),
  });
  const departments = useQuery({
    queryKey: ["departments", 100],
    queryFn: ({ signal }) => api.departmentsPage({ page_size: 100 }, signal),
  });
  const branches = useQuery({
    queryKey: ["branches"],
    queryFn: ({ signal }) => api.branches(signal),
  });
  const leaveTypes = useQuery({
    queryKey: ["setup-leave-types"],
    queryFn: ({ signal }) => api.leaveTypesPage({ page_size: 100 }, signal),
  });
  const shiftTemplates = useQuery({
    queryKey: ["setup-shift-templates"],
    queryFn: ({ signal }) => api.shiftTemplatesPage({ page_size: 100 }, signal),
  });

  const complete = useMutation({
    mutationFn: () => api.completeOnboarding(accountId!),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["account", accountId] });
      navigate("/", { replace: true });
    },
  });

  const activeTeam = (employees.data ?? []).filter((e) => e.status !== "exited");
  const done: Record<StepId, boolean> = {
    business: Boolean(account.data?.legal_name),
    organisation: (departments.data?.results.length ?? 0) > 0 && (branches.data?.length ?? 0) > 0,
    team: activeTeam.length > 1,
    shifts: (shiftTemplates.data?.results.length ?? 0) > 0,
    leave: (leaveTypes.data?.results.length ?? 0) > 0,
    review: true,
  };

  const loading =
    account.isPending ||
    employees.isPending ||
    departments.isPending ||
    branches.isPending ||
    leaveTypes.isPending ||
    shiftTemplates.isPending;

  const [chosen, setChosen] = useState<number | null>(null);
  // Resume at the first step whose records are missing until the owner picks a
  // step themselves. Derived from saved data, so a refresh or a fresh login
  // resumes at the right place with nothing extra to persist.
  const firstOpen = STEP_ORDER.findIndex((id) => id !== "review" && !done[id]);
  const autoIndex = firstOpen === -1 ? STEP_ORDER.indexOf("review") : firstOpen;
  const index = chosen ?? (loading ? 0 : autoIndex);

  // A finished account never sees the wizard again.
  if (account.data?.onboarding_state === "completed") {
    return <Navigate to="/" replace />;
  }

  const step = STEP_ORDER[index]!;
  const requiredRemaining = STEP_ORDER.filter((id) => !STEP_META[id].optional && id !== "review" && !done[id]).length;

  const goto = (next: number) => setChosen(Math.max(0, Math.min(STEP_ORDER.length - 1, next)));

  return (
    <>
      <PageHeader
        title="Set up your business"
        subtitle="A few steps to get SBM One ready. You can pause and finish later."
      />

      <div className="mb-4 flex items-center gap-2">
        {STEP_ORDER.map((id, i) => (
          <span
            key={id}
            className={`h-1.5 flex-1 rounded-full ${i <= index ? "bg-slate-900" : "bg-slate-200"}`}
            aria-hidden
          />
        ))}
      </div>

      <Card>
        <div className="mb-1 flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-slate-500">
          {`Step ${index + 1} of ${STEP_ORDER.length}`}
          {STEP_META[step].optional ? (
            <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] tracking-wide text-slate-500">
              Optional
            </span>
          ) : (
            <span className="rounded bg-amber-50 px-1.5 py-0.5 text-[10px] tracking-wide text-amber-700">
              Required
            </span>
          )}
        </div>
        <h2 className="text-lg font-semibold text-slate-900">{STEP_META[step].title}</h2>
        <p className="mb-4 mt-1 text-sm text-slate-500">{STEP_META[step].body}</p>

        {loading ? (
          <Skeleton className="h-32 w-full" />
        ) : (
          <>
            {step === "business" ? (
              <BusinessStep accountId={accountId!} account={account.data!} onSaved={() => goto(index + 1)} />
            ) : null}
            {step === "organisation" ? (
              <OrganisationStep
                departments={departments.data?.results ?? []}
                branches={branches.data ?? []}
              />
            ) : null}
            {step === "team" ? <TeamStep count={activeTeam.length} /> : null}
            {step === "shifts" ? <ShiftsStep count={shiftTemplates.data?.results.length ?? 0} /> : null}
            {step === "leave" ? <LeaveStep count={leaveTypes.data?.results.length ?? 0} /> : null}
            {step === "review" ? (
              <ReviewStep done={done} onFinish={() => complete.mutate()} finishing={complete.isPending} canFinish={requiredRemaining === 0} />
            ) : null}
          </>
        )}

        <div className="mt-6 flex items-center justify-between border-t border-slate-100 pt-4">
          <Button variant="ghost" onClick={() => goto(index - 1)} disabled={index === 0}>
            <ArrowLeft size={15} /> Back
          </Button>
          {step !== "review" && step !== "business" ? (
            <Button variant="primary" onClick={() => goto(index + 1)}>
              Continue <ArrowRight size={15} />
            </Button>
          ) : null}
        </div>
      </Card>

      <p className="mt-4 text-xs text-slate-400">
        Prefer to set this up yourself? Everything is also reachable from{" "}
        <Link to="/" className="underline">
          My Desk
        </Link>
        .
      </p>
    </>
  );
}

function BusinessStep({
  accountId,
  account,
  onSaved,
}: {
  accountId: string;
  account: Record<string, unknown>;
  onSaved: () => void;
}) {
  const queryClient = useQueryClient();
  const [fieldErrors, setFieldErrors] = useState<BusinessDetailsErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: (values: BusinessDetailsValues) =>
      submitBusinessDetails(values, (body) => api.updateAccount(accountId, body)),
    onSuccess: (result) => {
      if (!result.ok) {
        setFieldErrors(result.fieldErrors);
        setFormError(result.message);
        return;
      }
      setFieldErrors({});
      setFormError(null);
      void queryClient.invalidateQueries({ queryKey: ["account", accountId] });
      onSaved();
    },
  });
  const value = (key: string) => String(account[key] ?? "");
  const clearErrors = () => {
    if (Object.keys(fieldErrors).length > 0) setFieldErrors({});
    if (formError) setFormError(null);
  };
  return (
    <form
      className="grid gap-4 sm:grid-cols-2"
      noValidate
      onInput={clearErrors}
      onSubmit={(event) => {
        event.preventDefault();
        const data = new FormData(event.currentTarget);
        save.mutate(businessDetailsValues(Object.fromEntries(data.entries())));
      }}
    >
      <WizardField label="Legal name" hint="Used on invoices. Required." errors={fieldErrors.legal_name}>
        <input name="legal_name" defaultValue={value("legal_name")} required className={inputClass} />
      </WizardField>
      <WizardField label="Phone" errors={fieldErrors.phone}>
        <input name="phone" defaultValue={value("phone")} className={inputClass} />
      </WizardField>
      <WizardField
        label="Website"
        hint="Optional. If you omit https://, we add it for you."
        errors={fieldErrors.website}
      >
        <input name="website" defaultValue={value("website")} className={inputClass} />
      </WizardField>
      <WizardField label="Billing email" errors={fieldErrors.billing_email}>
        <input name="billing_email" defaultValue={value("billing_email")} className={inputClass} />
      </WizardField>
      <WizardField label="Tax identifier" hint="GSTIN in India." errors={fieldErrors.tax_identifier}>
        <input name="tax_identifier" defaultValue={value("tax_identifier")} className={inputClass} />
      </WizardField>
      <WizardField
        label="Country code"
        hint="Two-letter ISO, e.g. IN."
        errors={fieldErrors.country_code}
      >
        <input name="country_code" defaultValue={value("country_code")} className={inputClass} />
      </WizardField>
      <WizardField label="Address line 1" errors={fieldErrors.address_line1}>
        <input name="address_line1" defaultValue={value("address_line1")} className={inputClass} />
      </WizardField>
      <WizardField label="City" errors={fieldErrors.city}>
        <input name="city" defaultValue={value("city")} className={inputClass} />
      </WizardField>
      <WizardField label="State" errors={fieldErrors.state}>
        <input name="state" defaultValue={value("state")} className={inputClass} />
      </WizardField>
      <WizardField label="Postal code" errors={fieldErrors.postal_code}>
        <input name="postal_code" defaultValue={value("postal_code")} className={inputClass} />
      </WizardField>
      <div className="sm:col-span-2">
        {formError ? (
          <p role="alert" className="mb-2 text-sm text-rose-600">
            {formError}
          </p>
        ) : null}
        <Button type="submit" variant="primary" disabled={save.isPending}>
          {save.isPending ? "Saving�?�" : "Save and continue"}
        </Button>
      </div>
    </form>
  );
}

function OrganisationStep({
  departments,
  branches,
}: {
  departments: Array<{ id: string; name: string }>;
  branches: Array<{ id: string; name: string }>;
}) {
  const queryClient = useQueryClient();
  const [deptName, setDeptName] = useState("");
  const [branchName, setBranchName] = useState("");
  const addDepartment = useMutation({
    mutationFn: () => api.createDepartment({ name: deptName.trim() }),
    onSuccess: () => {
      setDeptName("");
      void queryClient.invalidateQueries({ queryKey: ["departments"] });
    },
  });
  const addBranch = useMutation({
    mutationFn: () => api.createBranch({ name: branchName.trim(), is_default: branches.length === 0 }),
    onSuccess: () => {
      setBranchName("");
      void queryClient.invalidateQueries({ queryKey: ["branches"] });
    },
  });
  return (
    <div className="grid gap-6 sm:grid-cols-2">
      <div>
        <h3 className="mb-2 text-sm font-medium text-slate-800">Departments</h3>
        <ul className="mb-3 space-y-1 text-sm text-slate-600">
          {departments.length === 0 ? <li className="text-slate-400">None yet.</li> : null}
          {departments.map((d) => (
            <li key={d.id}>{d.name}</li>
          ))}
        </ul>
        <div className="flex gap-2">
          <input
            value={deptName}
            onChange={(e) => setDeptName(e.target.value)}
            placeholder="e.g. Therapists"
            className={inputClass}
          />
          <Button
            variant="secondary"
            disabled={!deptName.trim() || addDepartment.isPending}
            onClick={() => addDepartment.mutate()}
          >
            Add
          </Button>
        </div>
      </div>
      <div>
        <h3 className="mb-2 text-sm font-medium text-slate-800">Work locations</h3>
        <ul className="mb-3 space-y-1 text-sm text-slate-600">
          {branches.length === 0 ? <li className="text-slate-400">None yet.</li> : null}
          {branches.map((b) => (
            <li key={b.id}>{b.name}</li>
          ))}
        </ul>
        <div className="flex gap-2">
          <input
            value={branchName}
            onChange={(e) => setBranchName(e.target.value)}
            placeholder="e.g. Main branch"
            className={inputClass}
          />
          <Button
            variant="secondary"
            disabled={!branchName.trim() || addBranch.isPending}
            onClick={() => addBranch.mutate()}
          >
            Add
          </Button>
        </div>
      </div>
    </div>
  );
}

function TeamStep({ count }: { count: number }) {
  return (
    <div className="text-sm text-slate-600">
      <p className="mb-3">
        {count > 1
          ? `You have ${count} people, including yourself.`
          : "Only your owner record exists so far. Add the people who work with you."}
      </p>
      <Link to="/people/employees/new" className="font-medium text-slate-900 underline">
        Add a team member →
      </Link>
    </div>
  );
}

function ShiftsStep({ count }: { count: number }) {
  const queryClient = useQueryClient();
  const create = useMutation({
    mutationFn: () => api.createShiftTemplate({
      name: "General",
      code: "GEN",
      start_time: "09:00:00",
      end_time: "18:00:00",
    }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["setup-shift-templates"] }),
  });
  return (
    <div className="text-sm text-slate-600">
      <p className="mb-3">
        {count > 0 ? `${count} shift template${count === 1 ? "" : "s"} configured.` : "No shifts configured yet."}
      </p>
      {count === 0 ? (
        <div className="flex flex-wrap gap-3">
          <Button variant="secondary" disabled={create.isPending} onClick={() => create.mutate()}>
            Create a “General 9–6” shift
          </Button>
          <Link to="/people/roster" className="self-center font-medium text-slate-900 underline">
            Open Roster →
          </Link>
        </div>
      ) : (
        <Link to="/people/roster" className="font-medium text-slate-900 underline">
          Manage shifts in Roster →
        </Link>
      )}
    </div>
  );
}

function LeaveStep({ count }: { count: number }) {
  return (
    <div className="text-sm text-slate-600">
      <p className="mb-3">
        {count > 0
          ? `${count} leave types are configured, so your team can request leave.`
          : "No leave types yet."}
      </p>
      <Link to="/people/leave?tab=types" className="font-medium text-slate-900 underline">
        Review leave types →
      </Link>
    </div>
  );
}

function ReviewStep({
  done,
  onFinish,
  finishing,
  canFinish,
}: {
  done: Record<StepId, boolean>;
  onFinish: () => void;
  finishing: boolean;
  canFinish: boolean;
}) {
  return (
    <div>
      <ul className="mb-4 divide-y divide-slate-100">
        {STEP_ORDER.filter((id) => id !== "review").map((id) => (
          <li key={id} className="flex items-center gap-3 py-2.5 text-sm">
            {done[id] ? (
              <CheckCircle2 size={18} className="text-emerald-600" />
            ) : (
              <Circle size={18} className="text-slate-300" />
            )}
            <span className={done[id] ? "text-slate-800" : "text-slate-500"}>{STEP_META[id].title}</span>
            <span className="ml-auto text-xs text-slate-400">
              {done[id] ? "Done" : STEP_META[id].optional ? "Postponed" : "Required"}
            </span>
          </li>
        ))}
      </ul>
      {!canFinish ? (
        <p className="mb-3 text-sm text-amber-700">
          Finish the required step (business details) before completing setup.
        </p>
      ) : null}
      <Button variant="primary" disabled={!canFinish || finishing} onClick={onFinish}>
        {finishing ? "Finishing…" : "Finish setup"}
      </Button>
    </div>
  );
}

function WizardField({
  label,
  hint,
  errors,
  children,
}: {
  label: string;
  hint?: string;
  errors?: string[] | undefined;
  children: React.ReactNode;
}) {
  const invalid = Boolean(errors?.length);
  const marked = Children.map(children, (child) => {
    if (!isValidElement(child)) return child;
    if (child.type !== "input") return child;
    const props = child.props as { className?: string };
    return cloneElement(
      child as React.ReactElement<{ className?: string; "aria-invalid"?: boolean }>,
      {
        "aria-invalid": invalid ? true : undefined,
        className: cx(props.className, invalid && invalidInputClass),
      },
    );
  });

  return (
    <label className="block text-sm">
      <span className="mb-1 block text-xs font-medium text-slate-600">{label}</span>
      {marked}
      {invalid ? (
        <span role="alert" className="mt-1 block text-sm text-rose-600">
          {errors?.join(" ")}
        </span>
      ) : hint ? (
        <span className="mt-1 block text-xs text-slate-400">{hint}</span>
      ) : null}
    </label>
  );
}
